import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", description: "", duration: "Biennio (1800-2000 ore)", active: true };

export default function CoursesTab() {
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/courses").then(({ data }) => setCourses(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description, duration: c.duration, active: c.active }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/admin/courses/${editing.id}`, form);
        toast.success("Corso aggiornato");
      } else {
        await api.post("/admin/courses", form);
        toast.success("Corso creato");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/admin/courses/${c.id}`);
      toast.success("Corso disattivato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button data-testid="admin-add-course-btn" className="bg-[#003366] hover:bg-[#002244]" onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" /> Nuovo corso
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-courses-list">
        {courses.map((c) => (
          <Card key={c.id} className="institutional-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display font-semibold text-slate-900">{c.name}</h3>
                  <p className="text-sm text-slate-600 mt-1">{c.description}</p>
                  <div className="mt-2 flex gap-2">
                    <Badge variant="secondary">{c.duration}</Badge>
                    <Badge className={c.active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}>{c.active ? "Attivo" : "Disattivato"}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button data-testid={`course-edit-${c.id}`} variant="ghost" size="sm" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`course-delete-${c.id}`} variant="ghost" size="sm" onClick={() => remove(c)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="course-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifica corso" : "Nuovo corso"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome del corso</Label>
              <Input data-testid="course-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Input data-testid="course-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Durata</Label>
              <Input data-testid="course-form-duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="course-form-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>Corso attivo (visibile ai candidati)</Label>
            </div>
            <Button data-testid="course-form-save" className="w-full bg-[#003366] hover:bg-[#002244]" disabled={!form.name.trim()} onClick={save}>
              Salva
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
